import React from 'react';
import { Card } from '../Card/Card.web';
import { SkeletonBlock } from '../SkeletonBlock/SkeletonBlock.web';
import { SkeletonText } from '../SkeletonText/SkeletonText.web';
import { progressListCardLayout as layout } from './ProgressListCardLayout.web';
import type { ProgressListCardSkeletonProps } from './ProgressListCardSkeleton';
import { ProgressListCardSkeletonCallout } from './ProgressListCardSkeletonCallout.web';
import {
  PROGRESS_LIST_CARD_SEGMENT_GAP,
  PROGRESS_LIST_CARD_SEGMENT_HEIGHT,
  PROGRESS_LIST_CARD_DOT_SIZE,
  PROGRESS_LIST_CARD_GLYPH_SIZE,
  PROGRESS_LIST_CARD_LABEL_VARIANT,
  PROGRESS_LIST_CARD_EYEBROW_VARIANT,
  PROGRESS_LIST_CARD_ROW_TITLE_VARIANT,
  PROGRESS_LIST_CARD_BODY_VARIANT,
  PROGRESS_LIST_CARD_FOOTER_TITLE_VARIANT,
  PROGRESS_LIST_CARD_FOOTER_BODY_VARIANT,
} from './ProgressListCard';

export function ProgressListCardSkeleton({
  rows,
  footer,
  active = true,
  testID,
  'data-testid': dataTestId,
}: Omit<ProgressListCardSkeletonProps, 'active'> & { active?: boolean; 'data-testid'?: string }) {
  const rootId = dataTestId ?? testID;
  const id = (suffix: string) => (rootId ? `${rootId}-${suffix}` : undefined);
  return (
    <div aria-hidden="true" data-testid={dataTestId ?? testID}>
      <Card
        background="container"
        paddingVertical="none"
        paddingHorizontal="none"
        {...(footer ? { footerBand: 'container' as const } : {})}
      >
        <div style={layout.stack}>
          {rows.map((row, index) => (
            <div key={index} style={layout.row} data-testid={id(`row-${index}`)}>
              <div style={layout.row}>
                <SkeletonText
                  text={row.label}
                  variant={PROGRESS_LIST_CARD_LABEL_VARIANT}
                  active={active}
                />
                {row.indicator ? (
                  <div
                    style={{
                      width:
                        row.indicator.kind === 'dots'
                          ? row.indicator.total * PROGRESS_LIST_CARD_DOT_SIZE +
                            (row.indicator.total - 1) * PROGRESS_LIST_CARD_SEGMENT_GAP
                          : '100%',
                      maxWidth: '100%',
                    }}
                    data-testid={id(`row-${index}-indicator`)}
                  >
                    <SkeletonBlock
                      width="fill"
                      height={
                        row.indicator.kind === 'dots'
                          ? PROGRESS_LIST_CARD_DOT_SIZE
                          : PROGRESS_LIST_CARD_SEGMENT_HEIGHT
                      }
                      radius="rounded"
                      active={active}
                    />
                  </div>
                ) : null}
                {row.eyebrow || row.title ? (
                  <div style={layout.titleBlock}>
                    {row.eyebrow ? (
                      <SkeletonText
                        text={row.eyebrow}
                        variant={PROGRESS_LIST_CARD_EYEBROW_VARIANT}
                        active={active}
                      />
                    ) : null}
                    {row.title ? (
                      <SkeletonText
                        text={row.title}
                        variant={PROGRESS_LIST_CARD_ROW_TITLE_VARIANT}
                        active={active}
                      />
                    ) : null}
                  </div>
                ) : null}
                {row.body || row.bodyLines ? (
                  <SkeletonText
                    text={row.body}
                    shape="block"
                    lines={row.bodyLines}
                    variant={PROGRESS_LIST_CARD_BODY_VARIANT}
                    active={active}
                    data-testid={id(`row-${index}-body`)}
                  />
                ) : null}
              </div>
              {row.callout ? (
                <ProgressListCardSkeletonCallout
                  callout={row.callout}
                  active={active}
                  testID={id(`row-${index}-callout`)}
                />
              ) : null}
            </div>
          ))}
        </div>
        {footer ? (
          <div style={layout.footer} data-testid={id('footer')}>
            <SkeletonBlock
              width={PROGRESS_LIST_CARD_GLYPH_SIZE}
              height={PROGRESS_LIST_CARD_GLYPH_SIZE}
              radius="rounded"
              active={active}
            />
            <div style={layout.footerText}>
              <SkeletonText
                text={footer.title}
                variant={PROGRESS_LIST_CARD_FOOTER_TITLE_VARIANT}
                active={active}
              />
              <SkeletonText
                text={footer.body}
                shape="block"
                data-testid={id('footer-body')}
                lines={footer.bodyLines}
                variant={PROGRESS_LIST_CARD_FOOTER_BODY_VARIANT}
                active={active}
              />
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
